/**
 * `BilingualBlock` — P-THAI-01 at the screen. OWNER: web-frontend-engineer.
 *
 * LAW 31: *"then that crm will show the suit data to the manufacturer in both
 * english and in thai."* `domain/glossary.ts` built the terms, the signatures
 * and the per-sheet projection; the order payload has carried `bilingual` since
 * that build and nothing rendered it.
 *
 * ── THE MARKER IS THE WHOLE DESIGN ────────────────────────────────────────
 *
 * There is no machine translation anywhere in this product, and PLATFORM-1
 * decision 6's recorded default is why: a term goes into Thai when a NAMED
 * person who reads both has read the English and signed it. Everything else
 * prints the English WITH A SENTENCE SAYING SO — not a blank, not a dash, and
 * never a plausible-looking guess.
 *
 * That sentence is the server's (`NOT_YET` / `STALE` in `domain/glossary.ts`),
 * and it is rendered here rather than re-typed, because the day the rule changes
 * it must change in one place. Same for the count: the sheet says how much of
 * what a cutter is holding has been through a bilingual reader, as a number,
 * so nobody has to estimate it from the page.
 *
 * `lang="th"` is on the Thai and nothing else — a screen reader, a font stack
 * and a line-breaker all need to know, and none of them can tell from the
 * bytes alone.
 *
 * ── C5, 5 SEPTEMBER 2026 (STYLIST-1 fix wave): ONE LINE, THEN A DOOR ──────
 *
 * The block printed every term inline: on the seeded two-piece that was 93
 * rows, 11,663 px of the order page, below the sheet and above the money —
 * and the English of a term was the glossary's `en`, which for 190 of the
 * 194 option values in rules.json is the value's own id. A tailor scrolled
 * past "sb_2 · NOT YET IN THAI" ninety times to reach the history.
 *
 * Two changes, both structural:
 *
 *   · THE COUNT LEADS, THE TERMS WAIT BEHIND ONE ACT. The server's own
 *     sentence ("0 of 93 terms on this sheet are signed off in Thai…") stands
 *     as the block; the list is behind "Show the 93 terms", the figure read
 *     from the lines and never typed, with `aria-expanded` and a label that
 *     changes so the state is on the glass and not only in the tree (the C6
 *     lesson). On PAPER the list prints whole regardless — `.pt-bilingual[hidden]`
 *     is shown under `@media print` — because the cutter in Bangkok reading the
 *     sheet on paper is exactly who the terms are for.
 *   · THE TERM IS THE CATALOGUE'S WORD. `bilingualTermLabel` reads the term
 *     key the way `domain/glossary.ts` wrote it — `garment.*`, `option.*`,
 *     `value.*`, `commissionType.*` — and resolves the same copy key the cut
 *     sheet and the configurator print, so the English a reader signs against
 *     is the English on the sheet, not a machine id. Sentences (`pattern.*`,
 *     `sheet.*`) are the server's and print as they are. A key the catalogue
 *     lacks falls back to the server's `en` rather than throwing in render.
 */

import { useId, useState } from 'react';
import { hasCopy, t, tDynamic } from '@pt/copy';
import type { BilingualBlock as Block, BilingualLine, TicketWord } from '../lib/contracts.ts';
import { Button, FactLine, Statement } from './primitives.tsx';

/**
 * The English a person reads for a term. The key shapes are the glossary's
 * (`houseTerms()` in apps/server/src/domain/glossary.ts):
 *
 *   garment.{garment}                → global.garment.{garment}
 *   option.{garment}.{field}         → config.option.{garment}.{field}.label
 *   value.{garment}.{field}.{value}  → config.option.{garment}.{field}.value.{value}
 *   commissionType.{id}              → config.option.commission.type.value.{id}
 *   measurement.{field}              → meas.field.{field}.label (LAW 59: the
 *                                      record's and the sheet's own label)
 *   pattern.* · sheet.* · ticket.*   → the server's sentence, as it is
 */
export function bilingualTermLabel(line: Pick<BilingualLine, 'termKey' | 'en'>): string {
  const [kind, ...rest] = line.termKey.split('.');
  const word = (key: string): string => (hasCopy(key) ? tDynamic(key) : line.en);
  switch (kind) {
    case 'garment':
      return word(`global.garment.${rest.join('.')}`);
    case 'option':
      return word(`config.option.${rest.join('.')}.label`);
    case 'value': {
      /* An option id is `garment.field` — two segments — and the value is
         whatever follows, so a value that itself carries a dot survives. */
      const optionId = rest.slice(0, 2).join('.');
      const value = rest.slice(2).join('.');
      return word(`config.option.${optionId}.value.${value}`);
    }
    case 'commissionType':
      return word(`config.option.commission.type.value.${rest.join('.')}`);
    case 'measurement':
      return word(`meas.field.${rest.join('.')}.label`);
    default:
      return line.en;
  }
}

/**
 * One word, inline, bilingual the house's way (LAW 59, the cut tickets): the
 * English (`bilingualTermLabel`, so the catalogue's word and never a machine
 * id), then the signed Thai under `lang="th"`. Where nobody has signed it, a
 * small EN mark, and the glossary's own sentence for a screen reader (no
 * `title`: an iPad has no hover). A figure or a code the glossary never heard
 * of carries neither: nobody signs "42 cm".
 */
export function Word({ word }: { word: TicketWord }) {
  return (
    <span className="pt-word">
      <span className="pt-word__en">{bilingualTermLabel(word)}</span>
      {word.th !== null ? (
        <>
          {' '}
          <span className="pt-word__th" lang="th">
            {word.th}
          </span>
        </>
      ) : word.marker === null ? null : (
        <>
          <sup className="pt-word__mark" aria-hidden="true">
            {t('thai.mark.en')}
          </sup>
          <span className="pt-visually-hidden">{` ${word.marker}`}</span>
        </>
      )}
    </span>
  );
}

/** The disclosure's label: the count from the data, and the state in words. */
export function bilingualTermsAction(open: boolean, count: number): string {
  if (open) return t('thai.terms.hide');
  return count === 1 ? t('thai.terms.show.one') : t('thai.terms.show.many', { count: String(count) });
}

export function BilingualWorkOrder({
  block,
  onOpenGlossary,
  initiallyOpen = false,
}: {
  block: Block;
  onOpenGlossary?: (() => void) | undefined;
  /** Open on first render — for a surface that has already asked for the terms. */
  initiallyOpen?: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const listId = useId();
  if (block.lines.length === 0) return null;
  return (
    <section className="pt-section" aria-label={t('thai.heading')}>
      <h2 className="pt-cutsheet__blockTitle">{t('thai.heading')}</h2>
      <Statement>{t('thai.lede')}</Statement>
      {/* The count, from the server that did the counting. */}
      <FactLine>{block.statement}</FactLine>

      <div className="pt-actions pt-noprint">
        <Button aria-expanded={open} aria-controls={listId} onClick={() => setOpen((value) => !value)}>
          {bilingualTermsAction(open, block.lines.length)}
        </Button>
        {onOpenGlossary === undefined ? null : (
          <Button variant="secondary" onClick={onOpenGlossary}>
            {t('thai.open.action')}
          </Button>
        )}
      </div>

      {/* Hidden on the glass until asked for; printed whole on paper. */}
      <ul id={listId} className="pt-bilingual" hidden={!open}>
        {block.lines.map((line) => (
          <li key={line.termKey} className="pt-bilingual__row">
            <span className="pt-bilingual__label">{t('thai.line.en')}</span>
            <span className="pt-bilingual__en">{bilingualTermLabel(line)}</span>

            {line.th === null ? (
              /* The server's own marker. A cutter reads the English above and
                 is told, in words, why there is no Thai beside it. */
              <span className="pt-bilingual__marker">{line.marker}</span>
            ) : (
              <>
                <span className="pt-bilingual__label">{t('thai.line.th')}</span>
                <span className="pt-bilingual__th" lang="th">
                  {line.th}
                </span>
                {line.signedByName === null ? null : (
                  <span className="pt-bilingual__by">
                    {t('thai.signedBy', { authorName: line.signedByName })}
                  </span>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
